import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import BookmarksResponseSchema, { BookmarksResponseJSON } from "marketplace/bookmarks/_schema/bookmarks-response-schema";
import BookmarkedCompanySchema, { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import BookmarkedIndividualSchema, { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import BookmarkedTeamSchema, { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import Session from "users/__session/session";

export default class BookmarkAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create BookmarkAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async getAllBookmarksForUser(
    abortController?: AbortController
  ): Promise<BookmarksResponseJSON> {
    const url = new URL(`/marketplace/bookmarks`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return BookmarksResponseSchema.validateJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) 
        throw error;
      throw new BookmarksRequestError("getAllBookmarksForUser", error);
    }
  }

  public async getAllCompanyBookmarksForUser(
    abortController?: AbortController
  ): Promise<BookmarkedCompanyJSON[]> {
    const url = new URL(`/marketplace/company-bookmarks`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return BookmarkedCompanySchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) 
        throw error;
      throw new BookmarksRequestError("getAllCompanyBookmarksForUser", error);
    }
  }

  public async getAllIndividualBookmarksForUser(
    abortController?: AbortController
  ): Promise<BookmarkedIndividualJSON[]> {
    const url = new URL(`/marketplace/individual-bookmarks`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return BookmarkedIndividualSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) 
        throw error;
      throw new BookmarksRequestError("getAllIndividualBookmarksForUser", error);
    }
  }

  public async getAllTeamBookmarksForUser(
    abortController?: AbortController
  ): Promise<BookmarkedTeamJSON[]> {
    const url = new URL(`/marketplace/team-bookmarks`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return BookmarkedTeamSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) 
        throw error;
      throw new BookmarksRequestError("getAllTeamBookmarksForUser", error);
    }
  }

  public async createCompanyBookmark(
    companyId: Guid
  ): Promise<BookmarkedCompanyJSON> {
    const url = new URL(`/marketplace/company-bookmark/${companyId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
      return BookmarkedCompanySchema.validateJSON(response.data);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new BookmarkedCompanyNotFoundException(
          "Unable to create company bookmark: company not found."
        );
      }
      else
        throw error;
    }
  }

  public async createIndividualBookmark(
    individualId: Guid
  ): Promise<BookmarkedIndividualJSON> {
    const url = new URL(`/marketplace/individual-bookmark/${individualId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
      return BookmarkedIndividualSchema.validateJSON(response.data);
    } catch (error) {
      if (
        axios.isAxiosError(error) && 
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new BookmarkedIndividualNotFoundException(
          "Unable to create individual bookmark: individual not found."
        );
      }
      else
        throw error;
    }
  }

  public async createTeamBookmark(teamId: Guid): Promise<BookmarkedTeamJSON> {
    const url = new URL(`/marketplace/team-bookmark/${teamId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers
        }
      );
      return BookmarkedTeamSchema.validateJSON(response.data);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        throw new BookmarkedTeamNotFoundException(
          "Unable to create vendor-team bookmark: vendor-team not found."
        );
      } else {
        throw error;
      }
    }
  }

  public async deleteCompanyBookmark(id: Guid): Promise<void> {
    const url = new URL(`/marketplace/company-bookmark/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.headers
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new BookmarkedCompanyNotFoundException(
          "Unable to delete company bookmark: bookmark not found."
        );
      else
        throw error;
    }
  }

  public async deleteIndividualBookmark(id: Guid): Promise<void> {
    const url = new URL(`/marketplace/individual-bookmark/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.headers
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new BookmarkedIndividualNotFoundException(
          "Unable to delete individual bookmark: bookmark not found."
        );
      else
        throw error;
    }
  }

  public async deleteTeamBookmark(id: Guid): Promise<void> {
    const url = new URL(`/marketplace/team-bookmark/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.headers
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new BookmarkedTeamNotFoundException(
          "Unable to delete vendor-team bookmark: bookmark not found."
        );
      else
        throw error;
    }
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();
}

export class BookmarkedCompanyNotFoundException extends Error { }
export class BookmarkedIndividualNotFoundException extends Error { }
export class BookmarkedTeamNotFoundException extends Error { }

export class BookmarksRequestError extends Error {
  endpoint: string;
  error: unknown;
  constructor(endpoint: string, error: unknown) {
    super(`Unable to get bookmarks from ${endpoint}: ${error}`);
    this.endpoint = endpoint;
    this.error = error;
  }
}
